import useFetch from '../../../hooks/useFetch';
import { useState } from 'react';

import { ServiceEvents } from '../shared/interfaces';
import { EndPoints } from '../EndPoints';
import {
    CheckingAccount,
    CheckingAccountDataForm,
    CheckingAccountFilters
} from '../../models/CheckingAccount';
import { CheckingAccountHistoryDataForm, CheckingAccountHistoryFilter } from '../../models/CheckingAccountHistory';

const useCheckingAccountService = () => {
    const { doGet, doPost, doPut, doDelete } = useFetch();

    const [fetchingGetCheckingAccounts, setFetchingGetCheckingAccounts] = useState(false);
    const [fetchingGetCheckingAccountsByWorker, setFetchingGetCheckingAccountsByWorker] = useState(false);
    const [fetchingShowCheckingAccount, setFetchingShowCheckingAccount] = useState(false);
    const [fetchingCreateCheckingAccount, setFetchingCreateCheckingAccount] = useState(false);
    const [fetchingStoreCheckingAccount, setFetchingStoreCheckingAccount] = useState(false);
    const [fetchingEditCheckingAccount, setFetchingEditCheckingAccount] = useState(false);
    const [fetchingUpdateCheckingAccount, setFetchingUpdateCheckingAccount] = useState(false);
    const [fetchingDeleteCheckingAccount, setFetchingDeleteCheckingAccount] = useState(false);
    const [fetchingActiveCheckingAccount, setFetchingActiveCheckingAccount] = useState(false);
    const [fetchingApprovedCheckingAccount, setFetchingApprovedCheckingAccount] = useState(false); 
    const [fetchingRejectCheckingAccount, setFetchingRejectCheckingAccount] = useState(false);
    const [fetchingGetCheckingAccountHistory, setFetchingGetCheckingAccountHistory] = useState(false);
    const [fetchingGetCheckingAccountTotalByWorker, setFetchingGetCheckingAccountTotalByWorker] = useState(false);

    const getCheckingAccounts = (
        CheckingAccountFilter: CheckingAccountFilters,
        events: ServiceEvents = {}
    ) => {
        const queryString = Object.entries(CheckingAccountFilter)
            .map(([key, value]) => `${key}=${value}`)
            .join('&');

        const url = `${EndPoints.CHECKING_ACCOUNTS.GET_CHECKING_ACCOUNTS}?${queryString}`;
        doGet({
            ...events,
            url: url,
            setFetching: setFetchingGetCheckingAccounts
        });
    };

    const getCheckingAccountsByWorker = (
        CheckingAccountFilter: CheckingAccountFilters,
        events: ServiceEvents = {}
    ) => {
        const queryString = Object.entries(CheckingAccountFilter)
            .map(([key, value]) => `${key}=${value}`)
            .join('&');

        const url = `${EndPoints.CHECKING_ACCOUNTS.GET_CHECKING_ACCOUNT_BY_WORKER}?${queryString}`;
        doGet({
            ...events,
            url: url,
            setFetching: setFetchingGetCheckingAccountsByWorker
        });
    };

    const showCheckingAccount = (id: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: EndPoints.CHECKING_ACCOUNTS.SHOW_CHECKING_ACCOUNT.replace(':id', id.toString()),
            setFetching: setFetchingShowCheckingAccount
        });
    };

    const createCheckingAccount = (events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: EndPoints.CHECKING_ACCOUNTS.CREATE_CHECKING_ACCOUNT,
            setFetching: setFetchingCreateCheckingAccount
        });
    };

    const storeCheckingAccount = (
        checkingAccount: CheckingAccountDataForm,
        events: ServiceEvents = {}
    ) => {
        doPost({
            ...events,
            url: EndPoints.CHECKING_ACCOUNTS.STORE_CHECKING_ACCOUNT,
            body: checkingAccount,
            setFetching: setFetchingStoreCheckingAccount
        });
    };

    const editCheckingAccount = (id: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: EndPoints.CHECKING_ACCOUNTS.EDIT_CHECKING_ACCOUNT.replace(':id', id.toString()),
            setFetching: setFetchingEditCheckingAccount
        });
    };

    const updateCheckingAccount = (
        id: number,
        checkingAccount: CheckingAccountDataForm,
        events: ServiceEvents = {}
    ) => {
        doPut({
            ...events,
            url: EndPoints.CHECKING_ACCOUNTS.UPDATE_CHECKING_ACCOUNT.replace(':id', id.toString()),
            body: checkingAccount,
            setFetching: setFetchingUpdateCheckingAccount
        });
    };

    const deleteCheckingAccount = (id: number, events: ServiceEvents = {}) => {
        doDelete({
            ...events,
            url: EndPoints.CHECKING_ACCOUNTS.DELETE_CHECKING_ACCOUNT.replace(':id', id.toString()),
            setFetching: setFetchingDeleteCheckingAccount
        });
    };

    const activeCheckingAccount = (id: number, active: boolean, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            body: { active: active },
            url: EndPoints.CHECKING_ACCOUNTS.ACTIVE_CHECKING_ACCOUNT.replace(':id', id.toString()),
            setFetching: setFetchingActiveCheckingAccount
        });
    };


    const approvedCheckingAccount = (checkingAccountId: number,
         events: ServiceEvents,
         formData: CheckingAccountHistoryDataForm = {}) => {
        doPut({
            ...events,
            url: EndPoints.CHECKING_ACCOUNTS.APPROVED_CHECKING_ACCOUNT.replace(':id', checkingAccountId.toString()),
            body:formData,
            setFetching: setFetchingApprovedCheckingAccount
        });
    };

    const rejectCheckingAccount = (checkingAccountId: number, 
        events: ServiceEvents,
        formData: CheckingAccountHistoryDataForm = {}) => {
        doPut({
            ...events,
            url: EndPoints.CHECKING_ACCOUNTS.REJECTED_CHECKING_ACCOUNT.replace(':id', checkingAccountId.toString()),
            body: formData,
            setFetching: setFetchingRejectCheckingAccount
        });
    };

    const getCheckingAccountHistory = (
        checkingAccountId: number,
        historyFilter: CheckingAccountHistoryFilter,
        events: ServiceEvents = {}
    ) => {
        const queryString = Object.entries(historyFilter)
            .map(([key, value]) => `${key}=${value}`)
            .join('&');
    
        const url = `${EndPoints.CHECKING_ACCOUNTS.GET_CHECKING_ACCOUNT_HISTORY.replace(':id', checkingAccountId.toString())}?${queryString}`;
    
        doGet({
            ...events,
            url: url,
            setFetching: setFetchingGetCheckingAccountHistory
        });
    };


    const getCheckingAccountTotalByWorker = (
        historyFilter: CheckingAccountFilters,
        events: ServiceEvents = {}
    ) => {
        const queryString = Object.entries(historyFilter)
            .map(([key, value]) => `${key}=${value}`)
            .join('&');
    
        const url = `${EndPoints.CHECKING_ACCOUNTS.GET_CHECKING_ACCOUNT_TOTAL_BY_WORKER}?${queryString}`;
    
        doGet({
            ...events,
            url: url,
            setFetching: setFetchingGetCheckingAccountTotalByWorker
        });
    };


    return {
        fetchingGetCheckingAccounts,
        fetchingCreateCheckingAccount,
        fetchingShowCheckingAccount,
        fetchingStoreCheckingAccount,
        fetchingEditCheckingAccount,
        fetchingUpdateCheckingAccount,
        fetchingDeleteCheckingAccount,
        fetchingApprovedCheckingAccount, 
        fetchingRejectCheckingAccount,
        fetchingGetCheckingAccountHistory,
        fetchingGetCheckingAccountTotalByWorker,
        fetchingGetCheckingAccountsByWorker,
        getCheckingAccounts,
        createCheckingAccount,
        activeCheckingAccount,
        showCheckingAccount,
        storeCheckingAccount,
        editCheckingAccount,
        updateCheckingAccount,
        deleteCheckingAccount,
        approvedCheckingAccount,  
        rejectCheckingAccount,
        getCheckingAccountHistory,
        getCheckingAccountTotalByWorker,
        getCheckingAccountsByWorker
    };
};

export default useCheckingAccountService;
